import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchMeals,
  createMeal,
  fetchMealById,
  updateMeal,
  deleteMeal,
  addMealComponent,
  updateMealComponent,
  deleteMealComponent,
  reorderMealComponents,
  duplicateMeal
} from '../../api/api_meals';

export const fetchMealsThunk = createAsyncThunk(
  'meals/fetchMeals',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchMeals(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const createMealThunk = createAsyncThunk(
  'meals/createMeal',
  async (mealData, { rejectWithValue }) => {
    try {
      const response = await createMeal(mealData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchMealByIdThunk = createAsyncThunk(
  'meals/fetchMealById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchMealById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updateMealThunk = createAsyncThunk(
  'meals/updateMeal',
  async ({ id, mealData }, { rejectWithValue }) => {
    try {
      const response = await updateMeal(id, mealData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deleteMealThunk = createAsyncThunk(
  'meals/deleteMeal',
  async (id, { rejectWithValue }) => {
    try {
      await deleteMeal(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const addMealComponentThunk = createAsyncThunk(
  'meals/addMealComponent',
  async ({ mealId, componentData }, { rejectWithValue }) => {
    try {
      const response = await addMealComponent(mealId, componentData);
      return { mealId, component: response.data.data || response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updateMealComponentThunk = createAsyncThunk(
  'meals/updateMealComponent',
  async ({ mealId, componentId, componentData }, { rejectWithValue }) => {
    try {
      const response = await updateMealComponent(mealId, componentId, componentData);
      return { mealId, component: response.data.data || response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deleteMealComponentThunk = createAsyncThunk(
  'meals/deleteMealComponent',
  async ({ mealId, componentId }, { rejectWithValue }) => {
    try {
      await deleteMealComponent(mealId, componentId);
      return { mealId, componentId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const reorderMealComponentsThunk = createAsyncThunk(
  'meals/reorderMealComponents',
  async ({ mealId, componentIds }, { rejectWithValue }) => {
    try {
      await reorderMealComponents(mealId, componentIds);
      return { mealId, componentIds };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const duplicateMealThunk = createAsyncThunk(
  'meals/duplicateMeal',
  async ({ mealId, mealData }, { rejectWithValue }) => {
    try {
      const response = await duplicateMeal(mealId, mealData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

const mealsSlice = createSlice({
  name: 'meals',
  initialState: {
    list: [],
    currentMeal: null,
    pagination: null,
    loading: false,
    error: null,
    status: 'idle',
  },
  reducers: {
    clearCurrentMeal: (state) => {
      state.currentMeal = null;
    },
    setCurrentMeal: (state, action) => {
      state.currentMeal = action.payload;
    },
    updateMealComponents: (state, action) => {
      if (state.currentMeal) {
        state.currentMeal.components = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch meals
      .addCase(fetchMealsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMealsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
        state.status = 'succeeded';
      })
      .addCase(fetchMealsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Create meal
      .addCase(createMealThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMealThunk.fulfilled, (state, action) => {
        state.loading = false;
        const newMeal = action.payload.data || action.payload;
        state.list.unshift(newMeal);
        state.status = 'succeeded';
      })
      .addCase(createMealThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Fetch meal by ID
      .addCase(fetchMealByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMealByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        const meal = action.payload.data || action.payload;
        
        // Ensure meal has calculated aggregates from backend
        // These should be calculated on backend, but ensure they exist
        if (!meal.nutrients) meal.nutrients = [];
        if (typeof meal.environment_average !== 'number') meal.environment_average = 0;
        if (typeof meal.health_average !== 'number') meal.health_average = 0;
        if (typeof meal.social_average !== 'number') meal.social_average = 0;
        if (!meal.original_contents) meal.original_contents = '';
        
        state.currentMeal = meal;
        if (meal && meal.components) {
          meal.components = meal.components.map((comp, idx) => {
            // Transform component data similar to dishes
            let name = comp.name || comp.dish_name || comp.dish?.name || 
                      comp.ingredient?.name || comp.product?.name || 'Okänd komponent';
            const amount = comp.weight_in_amount ?? comp.amount ?? 0;
            const percentage = comp.weight_in_percentage ?? comp.percentage ?? null;
            let unitAbbr = comp.unit ?? comp.unit_abbr ?? 'g';
            
            // Get origin/country information - backend should provide origin as country name
            // Backend should set origin, but if it's missing but we have origin_country_id, we could look it up
            // For now, just use what backend provides
            let origin = comp.origin || null;

            // Handle different source types
            let climate = null;
            let water = null;
            let health = null;
            let social = null;
            let sustainability_values = undefined;

            if (comp.source_type === 'product' || comp.sourceType === 'product') {
              // For products, extract scores from product object or calculated_parameter_object
              const product = comp.product || {};
              const calculatedParams = product.calculated_parameter_object || {};
              
              // Extract from calculated_parameter_object first, then fallback to direct values
              climate = calculatedParams.climate?.value ?? 
                       product.climate_score ?? 
                       comp.climate_score ?? 
                       null;
              water = calculatedParams.water?.value ?? 
                     product.water_score ?? 
                     comp.water_score ?? 
                     null;
              health = calculatedParams.health?.value ?? 
                      product.health_score ?? 
                      comp.health_score ?? 
                      null;
              social = calculatedParams.social_justice?.value ?? 
                      product.social_justice_score ?? 
                      comp.social_justice_score ?? 
                      null;
              
              // Get origin from product if not already set
              if (!origin && product.origin_country_id) {
                origin = product.origin_country_id; // Could be enhanced with country name lookup
              }
              
              // Ensure name is from product
              name = name || product.name || product.fullname || 'Produkt';
            } else if (comp.source_type === 'recipe' || comp.sourceType === 'recipe') {
              // For recipes/dishes, get from dish object
              const dish = comp.dish || {};
              climate = dish.climate_score ?? comp.climate_score ?? null;
              water = dish.water_score ?? comp.water_score ?? null;
              health = dish.health_score ?? comp.health_score ?? null;
              social = dish.social_justice_score ?? comp.social_justice_score ?? null;
              name = dish.name || name;
              // Get origin from component (backend should set it)
              if (!origin && comp.origin) {
                origin = comp.origin;
              }
            } else if (comp.source_type === 'ingredient' || comp.sourceType === 'ingredient') {
              // For ingredients, get from component or ingredient
              climate = comp.climate_score ?? comp.ingredient?.climate_score ?? null;
              water = comp.water_score ?? comp.ingredient?.water_score ?? null;
              health = comp.health_score ?? comp.ingredient?.health_score ?? null;
              social = comp.social_justice_score ?? comp.ingredient?.social_justice_score ?? null;
              name = comp.ingredient?.name || name;
            }

            sustainability_values = (climate ?? water ?? health ?? social) != null
              ? { climate, water, health, social }
              : undefined;

            // Extract all sustainability scores for the component
            // These need to be directly on the component object for SustainabilityValue and IngredientParametersModal to work
            const product = comp.product || {};
            const dish = comp.dish || {};
            const calculatedParams = product.calculated_parameter_object || {};
            
            // Get all scores - prioritize from calculated_parameter_object for products
            const allScores = {
              climate_score: comp.source_type === 'product' 
                ? (calculatedParams.climate?.value ?? product.climate_score ?? comp.climate_score ?? null)
                : (comp.source_type === 'recipe' 
                  ? (dish.climate_score ?? comp.climate_score ?? null)
                  : (comp.climate_score ?? comp.ingredient?.climate_score ?? null)),
              
              water_score: comp.source_type === 'product'
                ? (calculatedParams.water?.value ?? product.water_score ?? comp.water_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.water_score ?? comp.water_score ?? null)
                  : (comp.water_score ?? comp.ingredient?.water_score ?? null)),
              
              health_score: comp.source_type === 'product'
                ? (calculatedParams.health?.value ?? product.health_score ?? comp.health_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.health_score ?? comp.health_score ?? null)
                  : (comp.health_score ?? comp.ingredient?.health_score ?? null)),
              
              social_justice_score: comp.source_type === 'product'
                ? (calculatedParams.social_justice?.value ?? product.social_justice_score ?? comp.social_justice_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.social_justice_score ?? comp.social_justice_score ?? null)
                  : (comp.social_justice_score ?? comp.ingredient?.social_justice_score ?? null)),
              
              biodiversity_score: comp.source_type === 'product'
                ? (calculatedParams.biodiversity?.value ?? product.biodiversity_score ?? comp.biodiversity_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.biodiversity_score ?? comp.biodiversity_score ?? null)
                  : (comp.biodiversity_score ?? comp.ingredient?.biodiversity_score ?? null)),
              
              transport_score: comp.source_type === 'product'
                ? (calculatedParams.transport?.value ?? product.transport_score ?? comp.transport_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.transport_score ?? comp.transport_score ?? null)
                  : (comp.transport_score ?? comp.ingredient?.transport_score ?? null)),
              
              land_use_score: comp.source_type === 'product'
                ? (calculatedParams.land_use?.value ?? product.land_use_score ?? comp.land_use_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.land_use_score ?? comp.land_use_score ?? null)
                  : (comp.land_use_score ?? comp.ingredient?.land_use_score ?? null)),
              
              fertilizers_use_score: comp.source_type === 'product'
                ? (calculatedParams.fertilizer_use?.value ?? product.fertilizers_use_score ?? comp.fertilizers_use_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.fertilizers_use_score ?? comp.fertilizers_use_score ?? null)
                  : (comp.fertilizers_use_score ?? comp.ingredient?.fertilizers_use_score ?? null)),
              
              pesticides_use_score: comp.source_type === 'product'
                ? (calculatedParams.pesticide_use?.value ?? product.pesticides_use_score ?? comp.pesticides_use_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.pesticides_use_score ?? comp.pesticides_use_score ?? null)
                  : (comp.pesticides_use_score ?? comp.ingredient?.pesticides_use_score ?? null)),
              
              antibiotics_resistance_score: comp.source_type === 'product'
                ? (calculatedParams.antibiotics_resistance?.value ?? product.antibiotics_resistance_score ?? comp.antibiotics_resistance_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.antibiotics_resistance_score ?? comp.antibiotics_resistance_score ?? null)
                  : (comp.antibiotics_resistance_score ?? comp.ingredient?.antibiotics_resistance_score ?? null)),
              
              child_labor_score: comp.source_type === 'product'
                ? (calculatedParams.child_labor?.value ?? product.child_labor_score ?? comp.child_labor_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.child_labor_score ?? comp.child_labor_score ?? null)
                  : (comp.child_labor_score ?? comp.ingredient?.child_labor_score ?? null)),
              
              equality_score: comp.source_type === 'product'
                ? (calculatedParams.equality?.value ?? product.equality_score ?? comp.equality_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.equality_score ?? comp.equality_score ?? null)
                  : (comp.equality_score ?? comp.ingredient?.equality_score ?? null)),
              
              worker_rights_score: comp.source_type === 'product'
                ? (calculatedParams.worker_rights?.value ?? product.worker_rights_score ?? comp.worker_rights_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.worker_rights_score ?? comp.worker_rights_score ?? null)
                  : (comp.worker_rights_score ?? comp.ingredient?.worker_rights_score ?? null)),
              
              local_population_score: comp.source_type === 'product'
                ? (calculatedParams.local_population?.value ?? product.local_population_score ?? comp.local_population_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.local_population_score ?? comp.local_population_score ?? null)
                  : (comp.local_population_score ?? comp.ingredient?.local_population_score ?? null)),
              
              animal_protection_score: comp.source_type === 'product'
                ? (calculatedParams.world_animal_protection?.value ?? product.animal_protection_score ?? comp.animal_protection_score ?? null)
                : (comp.source_type === 'recipe'
                  ? (dish.animal_protection_score ?? comp.animal_protection_score ?? null)
                  : (comp.animal_protection_score ?? comp.ingredient?.animal_protection_score ?? null)),
              
              co2e_equivalent: comp.source_type === 'product'
                ? (calculatedParams.co2?.value ?? product.CO2e ?? comp.CO2e ?? comp.co2e_equivalent ?? null)
                : (comp.CO2e ?? comp.co2e_equivalent ?? null),
            };

            // Extract NOVA data based on source type
            let nova = null;
            let novaflag = null;
            
            if (comp.source_type === 'ingredient' || comp.sourceType === 'ingredient') {
              // For ingredients, get novaflag
              novaflag = comp.novaflag ?? comp.ingredient?.novaflag ?? null;
            } else if (comp.source_type === 'product' || comp.sourceType === 'product') {
              // For products, get nova (pre-calculated)
              nova = comp.nova ?? comp.product?.nova ?? null;
            } else if (comp.source_type === 'recipe' || comp.sourceType === 'recipe') {
              // For recipes, get nova (should be pre-calculated by backend)
              nova = comp.nova ?? comp.dish?.nova ?? null;
            }

            return {
              ...comp,
              ...allScores, // Spread all scores directly onto the component
              id: comp.id,
              name,
              amount,
              percentage,
              unit_abbr: unitAbbr,
              origin,
              position: comp.position ?? idx + 1,
              sourceType: comp.source_type || comp.sourceType,
              sourceId: comp.source_id || comp.sourceId,
              sustainability_values,
              nova, // Include NOVA score
              novaflag, // Include novaflag for ingredients
              // Preserve full product data
              product: comp.product || undefined,
              // Preserve full dish data if it's a recipe
              dish: comp.dish || comp.dish_name ? { 
                name: comp.dish?.name || comp.dish_name,
                ...comp.dish 
              } : undefined,
              // Preserve full ingredient data
              ingredient: comp.ingredient || undefined,
            };
          });
        }
        state.status = 'succeeded';
      })
      .addCase(fetchMealByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Update meal
      .addCase(updateMealThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMealThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedMeal = action.payload.data || action.payload;
        const index = state.list.findIndex(m => m.id === updatedMeal.id);
        if (index !== -1) {
          state.list[index] = updatedMeal;
        }
        if (state.currentMeal && state.currentMeal.id === updatedMeal.id) {
          state.currentMeal = { ...state.currentMeal, ...updatedMeal };
        }
        state.status = 'succeeded';
      })
      .addCase(updateMealThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Delete meal
      .addCase(deleteMealThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMealThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(m => m.id !== action.payload);
        if (state.currentMeal && state.currentMeal.id === action.payload) {
          state.currentMeal = null;
        }
        state.status = 'succeeded';
      })
      .addCase(deleteMealThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Add meal component
      .addCase(addMealComponentThunk.fulfilled, (state, action) => {
        if (state.currentMeal && state.currentMeal.id === action.payload.mealId) {
          if (!state.currentMeal.components) {
            state.currentMeal.components = [];
          }
          // Ensure origin is preserved from backend response
          const component = action.payload.component;
          // Backend should return origin as country name, but ensure it's there
          const newComponent = {
            ...component,
            origin: component.origin || null, // Explicitly preserve origin from backend
          };
          state.currentMeal.components.push(newComponent);
        }
      })
      // Update meal component
      .addCase(updateMealComponentThunk.fulfilled, (state, action) => {
        if (state.currentMeal && state.currentMeal.id === action.payload.mealId) {
          const index = state.currentMeal.components.findIndex(
            c => c.id === action.payload.component.id
          );
          if (index !== -1) {
            state.currentMeal.components[index] = action.payload.component;
          }
        }
      })
      // Delete meal component
      .addCase(deleteMealComponentThunk.fulfilled, (state, action) => {
        if (state.currentMeal && state.currentMeal.id === action.payload.mealId) {
          state.currentMeal.components = state.currentMeal.components.filter(
            c => c.id !== action.payload.componentId
          );
        }
      })
      // Reorder meal components
      .addCase(reorderMealComponentsThunk.fulfilled, (state, action) => {
        if (state.currentMeal && state.currentMeal.id === action.payload.mealId) {
          const orderedComponents = action.payload.componentIds.map((id, idx) => {
            const component = state.currentMeal.components.find(c => c.id === id);
            return component ? { ...component, position: idx + 1 } : null;
          }).filter(Boolean);
          state.currentMeal.components = orderedComponents;
        }
      })
      // Duplicate meal
      .addCase(duplicateMealThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicateMealThunk.fulfilled, (state, action) => {
        state.loading = false;
        const duplicatedMeal = action.payload.data || action.payload;
        state.list.unshift(duplicatedMeal);
        state.status = 'succeeded';
      })
      .addCase(duplicateMealThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      });
  },
});

export const { clearCurrentMeal, setCurrentMeal, updateMealComponents } = mealsSlice.actions;
export default mealsSlice.reducer;

